// @mops/i18n-v1 — 사전에서 화면 문구를 꺼내는 작은 도우미. 프레임워크와 무관한 순수 JS 다.
//
// 타입은 옆의 index.d.ts 가 준다(없는 키 = 컴파일 에러, `{자리}` 값 빠뜨림 = 컴파일 에러).
// 구현을 .ts 가 아니라 .js 로 둔 이유: 번들러 있는 바닐라 JS(b3x-pr 웹)·Node 시험이 빌드
// 단계 없이 그대로 import 한다. React 는 쓰지 않는다 — t 는 모듈 함수라 훅이 필요 없다.
//
// 넣지 않은 것(일부러): 복수형·숫자·날짜 서식·언어 감지. 화면 언어는 한국어 하나고,
// 언어를 바꾸는 서비스(dbix 의 en)는 언어마다 createT 를 하나씩 만들어 자기 쪽에서 고른다.

/** 이름 자리로 보는 글자. index.d.ts 의 WordChar 와 **같은 집합이어야 한다** — 타입 쪽이 더
 *  넓으면 예시 JSON 같은 평범한 중괄호가 자리로 읽혀 컴파일이 막힌다(term-mops 가 겪었다). */
const PLACEHOLDER = /\{(\w+)\}/g;

/**
 * 문장의 `{이름}` 자리를 채운다. 값이 없는 자리는 **그대로 둔다** — 지우면 「서버 개」처럼
 * 무엇이 빠졌는지 모르는 문장이 나가고, 남겨 두면 화면에서 바로 눈에 띈다.
 * @param {string} text
 * @param {Record<string, string | number>} [vars]
 */
export function format(text, vars) {
  if (!vars) return text;
  return text.replace(PLACEHOLDER, (whole, name) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : whole,
  );
}

/**
 * 중첩 사전을 「점으로 이은 키 → 문장」 한 장으로 편다. 이미 납작한 키(`"nav.menu.x"`)는
 * 그대로 남는다 — 그래서 term(중첩)·dbix(납작) 모양을 둘 다 받는다.
 * 잎이 문자열이 아닌 것(숫자·함수·배열)은 사전이 아니다 — 조용히 버리지 않고 던진다.
 * @param {object} dict
 * @returns {Record<string, string>}
 */
export function flatten(dict, prefix = "", out = {}) {
  for (const [k, v] of Object.entries(dict)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (typeof v === "string") {
      if (Object.prototype.hasOwnProperty.call(out, key)) {
        throw new Error(`i18n: 같은 키가 두 번 있습니다 — ${key}`);
      }
      out[key] = v;
    } else if (v && typeof v === "object" && !Array.isArray(v)) {
      flatten(v, key, out);
    } else {
      throw new Error(`i18n: ${key} 의 값이 문자열도 묶음도 아닙니다(${Array.isArray(v) ? "배열" : typeof v})`);
    }
  }
  return out;
}

/** 없는 키를 한 번만 알린다. 같은 화면이 다시 그려질 때마다 콘솔이 차면 정작 새 경고가 묻힌다. */
function warnOnce() {
  const seen = new Set();
  return (key) => {
    if (seen.has(key)) return;
    seen.add(key);
    console.warn(`i18n: 사전에 없는 키 — ${key}`);
  };
}

/**
 * 사전 하나로 t() 를 만든다.
 *
 *   const t = createT(ko);
 *   t("common.action.save")                 // "저장"
 *   t("token.created", { name: "ci" })      // "ci 토큰을 만들었습니다"
 *
 * - 없는 키: 키 문자열을 그대로 돌려주고 콘솔에 한 번 경고한다(빈칸 금지 — 무엇이 빠졌는지 보여야 한다).
 *   타입이 막으므로 여기 오는 것은 문자열을 손으로 지어 부른 자리뿐이다.
 * - `fallback`: 이 사전에 없을 때 볼 두 번째 사전(다른 언어 팩이 덜 찼을 때 원문으로 메운다).
 * - `onMissing`: 경고 대신 부를 함수(시험·로그 수집용).
 * - `emptyIsMissing`: 빈 문자열 값을 「없음」으로 본다 — 언어 팩에서 아직 안 옮긴 칸을 `""` 로 두는 모양(term 의
 *   CSV 왕복)이면 켠다. 그러면 빈칸 대신 fallback(원문)이 나간다. 기본은 꺼짐 — 빈 문자열이 일부러 둔 값일 수 있다.
 *
 * @param {object} dict
 * @param {{ fallback?: object, onMissing?: (key: string) => void, emptyIsMissing?: boolean }} [opts]
 */
export function createT(dict, opts = {}) {
  const main = flatten(dict);
  const back = opts.fallback ? flatten(opts.fallback) : null;
  const missing = opts.onMissing ?? warnOnce();
  const usable = (table, key) =>
    Object.prototype.hasOwnProperty.call(table, key) && !(opts.emptyIsMissing && table[key] === "");

  const raw = (key) => {
    if (usable(main, key)) return main[key];
    if (back && usable(back, key)) return back[key];
    return undefined;
  };

  function t(key, vars) {
    const text = raw(key);
    if (text === undefined) {
      missing(key);
      return key;
    }
    return format(text, vars);
  }

  /** 컴파일 때 키를 모르는 자리 전용(서버 오류 코드 → 문장 등). 없으면 null — 경고하지 않는다.
   *  부르는 쪽이 「모르는 코드」와 「빈 문장」을 갈라 다뤄야 하기 때문이다. */
  t.dynamic = (key, vars) => {
    const text = raw(key);
    return text === undefined ? null : format(text, vars);
  };
  t.has = (key) => raw(key) !== undefined;
  return t;
}

/**
 * 문장을 `{이름}` 자리로 쪼갠다 — 글자 조각은 문자열로, 자리는 `{ name, value }` 로. 문장 안에 요소(`<code>`·링크)를
 * 끼우는 React 판(`@mops/i18n-v1/react` 의 `rich`)이 이것 위에 얇게 서 있다. 프레임워크가 다른 화면도 같은 쪼개기를 쓴다.
 * `values` 에 없는 자리는 글자 `{이름}` 그대로 남긴다 — 빠진 것이 화면에 보여야 한다(t() 와 같은 약속).
 * @param {string} text
 * @param {Record<string, unknown>} values
 * @returns {Array<string | { name: string, value: unknown }>}
 */
export function richParts(text, values) {
  const out = [];
  let last = 0;
  for (const m of text.matchAll(PLACEHOLDER)) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (Object.prototype.hasOwnProperty.call(values, m[1])) out.push({ name: m[1], value: values[m[1]] });
    else out.push(m[0]);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/**
 * `{이름}` 자리를 **그대로 남겨 두는** 값. t() 로 문장을 만들 때 요소가 들어갈 자리에 준다 —
 * `rich(t("k", { err, ...hole("url") }), { url: <code>…</code> })`. (b3x-in 이 만든 모양)
 * @template {string} N
 * @param {...N} names
 * @returns {Record<N, string>}
 */
export function hole(...names) {
  return Object.fromEntries(names.map((n) => [n, `{${n}}`]));
}

/**
 * 다른 언어 사전을 원문에 맞대 보는 자리 — 검사는 타입(index.d.ts 의 definePack)이 하고 실행 때는 받은 것을
 * 그대로 돌려준다. `export const en = definePack<typeof ko>()({ … })`.
 */
export function definePack() {
  return (pack) => pack;
}

export { commonKo } from "./common.ko.js";
export { commonEn } from "./common.en.js";
